import React from 'react';
import { Checkbox } from 'src/components/PulseUI';
import { daysSince } from 'src/utils/daysSince';
import type { Notification } from 'src/api/redux';

interface InboxItemProps {
  item: Notification;
  isChecked: boolean;
  isSelected?: boolean;
  onClick: () => void;
  onToggle: () => void;
}

const InboxItem: React.FC<InboxItemProps> = ({ item, isChecked, isSelected, onClick, onToggle }) => {
  const isUnread = !item.read_at;

  return (
    <button
      key={item.id}
      type="button"
      aria-current={isSelected ? 'true' : undefined}
      className={`flex w-full flex-col gap-1 border-b border-neutral-100 px-5 py-4 text-left transition-colors last:border-b-0 ${
        isSelected
          ? 'bg-sky-100'
          : isUnread
            ? 'bg-sky-50/40 hover:bg-sky-50'
            : 'hover:bg-neutral-50'
      }`}
      onClick={onClick}
      data-testid={`item-row-${item.id}`}
    >
      {/* Top row: checkbox, dot, title, timestamp */}
      <div className="flex w-full items-center gap-3">
        <Checkbox
          checked={isChecked}
          onChange={onToggle}
          size="sm"
          aria-label={`Select ${item.title}`}
          data-testid={`item-checkbox-${item.id}`}
        />
        <span
          aria-hidden="true"
          data-testid={`item-unread-dot-${item.id}`}
          className={`h-2.5 w-2.5 shrink-0 rounded-full ${
            isUnread ? 'bg-sky-500' : 'bg-transparent'
          }`}
        />
        <p
          className={`min-w-0 flex-1 truncate ${
            isUnread
              ? 'text-base font-semibold text-neutral-900'
              : 'text-base text-neutral-600'
          }`}
        >
          {item.title}
        </p>
        <span
          className={`shrink-0 text-xs ${
            isUnread ? 'font-semibold text-sky-600' : 'text-neutral-400'
          }`}
        >
          {daysSince(item.created_at)}
        </span>
      </div>

      {/* Body */}
      {item.body && (
        <p className="line-clamp-2 pl-[42px] text-sm text-neutral-500">
          {item.body}
        </p>
      )}
    </button>
  );
};

export default InboxItem;
